<template>
  <div class="container mt-5">
    <h4 class="mb-4" style="color: #b03a45;">
      <i class="fas fa-id-card me-2 text-warning"></i>
      {{ isEditMode ? $t("personalInfo.titleEdit") : $t("personalInfo.titleAdd") }}
    </h4>

    <form @submit.prevent="handleSubmit" enctype="multipart/form-data">
      <!-- Photo Upload -->
      <div class="text-center mb-4">
        <label class="form-label fw-bold text-secondary">{{ $t("personalInfo.profileImage") }}</label>
        <input
          type="file"
          class="form-control mx-auto custom-file-input"
          style="max-width: 300px;"
          @change="onPhotoChange"
          accept="image/*"
        />
        <div v-if="photoPreview" class="mt-3">
          <img
            :src="photoPreview"
            class="img-thumbnail"
            style="width: 120px; height: 120px; object-fit: cover; border-radius: 50%; border: 2px solid #b03a45;"
          />
        </div>
      </div>

      <!-- Input Fields -->
      <div class="row">
        <div v-for="field in fields" :key="field.name" class="col-md-6 mb-4">
          <div class="p-3 shadow-sm rounded border">
            <label class="form-label fw-bold" :style="{ color: '#b03a45' }">
              {{ $t(field.label) }}
            </label>

            <select
              v-if="field.type === 'select'"
              v-model="form[field.name]"
              class="form-select"
              v-bind="field.attrs"
            >
              <option disabled value="">{{ $t("employeesAdd.rolePlaceholder") }}</option>
              <option
  v-for="option in field.options"
  :key="option.value"
  :value="option.value"
>
  {{ $t(option.label) }}
</option>

            </select>

            <input
              v-else
              v-model="form[field.name]"
              class="form-control"
              v-bind="field.attrs"
            />
          </div>
        </div>
      </div>

      <!-- Buttons -->
      <div class="d-flex justify-content-between mt-4">
        <button type="button" class="btn btn-outline-secondary" @click="handleBack">
          {{ $t("personalInfo.buttons.back") }}
        </button>
        <div class="d-flex gap-3">
          <button
            v-if="isEditMode"
            type="button"
            class="btn"
            style="background-color: #b03a45; color: white;"
            @click="goNext"
          >
            <i class="fas fa-arrow-right"></i>
          </button>
          <button
            type="submit"
            class="btn text-white px-4"
            style="background-color: #b03a45;"
          >
            {{ isEditMode ? $t("personalInfo.buttons.save") : $t("personalInfo.buttons.next") }}
          </button>
        </div>
      </div>
    </form>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useToast } from 'vue-toastification'
import axios from '@/api'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const router = useRouter()
const route = useRoute()
const toast = useToast()

// === BASE URL API (ton .env) ===
const API_BASE = (process.env.VUE_APP_API_URL || '').replace(/\/+$/, '')

// Normalise TOUTES les URLs photo venant du back
const buildPhotoUrl = (photo) => {
  if (!photo) return null
  let p = String(photo).trim()

  const idx = p.indexOf('/storage/')
  if (idx !== -1) {
    p = p.slice(idx + '/storage/'.length)
  } else {
    p = p.replace(/^\/?storage\/?/, '')
  }

  return `${API_BASE}/storage/${p}`
}

const userId = String(route.query.user_id || '')
const isEditMode = computed(() => route.path.includes('/edit'))

const form = ref({
  user_id: userId,
  arabic_name: '',
  gender: '',
  date_of_birth: '',
  place_of_birth: '',
  place_of_birth_ar: '',
  nationality: '',
  address: '',
  address_ar: '',
  phone_number: '',
  marital_status: '',
  photo: null
})

const photoFile = ref(null)
const photoPreview = ref(null)

const fields = [
  {
    name: 'gender',
    label: 'personalInfo.fields.gender',
    type: 'select',
    options: [
      { value: 'Male', label: 'personalInfo.fields.male' },
      { value: 'Female', label: 'personalInfo.fields.female' }
    ],
    attrs: { required: true }
  },
  {
    name: 'date_of_birth',
    label: 'personalInfo.fields.dob',
    type: 'input',
    attrs: { type: 'date', required: true }
  },
  { name: 'arabic_name', label: 'personalInfo.fields.arabicName', type: 'input', attrs: { type: 'text' } },
  { name: 'place_of_birth', label: 'personalInfo.fields.placeOfBirth', type: 'input', attrs: { type: 'text' } },
  { name: 'place_of_birth_ar', label: 'personalInfo.fields.placeOfBirthAr', type: 'input', attrs: { type: 'text' } },
  { name: 'nationality', label: 'personalInfo.fields.nationality', type: 'input', attrs: { type: 'text' } },
  { name: 'address', label: 'personalInfo.fields.address', type: 'input', attrs: { type: 'text' } },
  { name: 'address_ar', label: 'personalInfo.fields.addressAr', type: 'input', attrs: { type: 'text' } },
  { name: 'phone_number', label: 'personalInfo.fields.phone', type: 'input', attrs: { type: 'text' } },
  {
    name: 'marital_status',
    label: 'personalInfo.fields.maritalStatus',
    type: 'select',
    options: [
      { value: 'Single', label: 'personalInfo.fields.single' },
      { value: 'Married', label: 'personalInfo.fields.married' },
      { value: 'Divorced', label: 'personalInfo.fields.divorced' },
      { value: 'Widowed', label: 'personalInfo.fields.widowed' }
    ],
    attrs: {}
  }
]

const onPhotoChange = (e) => {
  const file = e.target.files?.[0]
  if (file && file.type.startsWith('image/')) {
    photoFile.value = file
    photoPreview.value = URL.createObjectURL(file)
    form.value.photo = file
  } else {
    photoFile.value = null
    photoPreview.value = null
    form.value.photo = null
    toast.error(t('personalInfo.messages.invalidImage'))
  }
}

const fetchData = async () => {
  try {
    const { data } = await axios.get(`/employee/personal-information/${userId}`)
    if (data.data) {
      Object.assign(form.value, {
        ...data.data,
        user_id: userId
      })

      if (data.data.date_of_birth) {
        const rawDate = new Date(data.data.date_of_birth)
        form.value.date_of_birth = rawDate.toISOString().split('T')[0]
      }

      if (data.data.photo) {
        photoPreview.value = buildPhotoUrl(data.data.photo)
      }
    }
  } catch (err) {
    toast.error('Failed to load personal info.')
    console.error(err)
  }
}

const handleSubmit = async () => {
  try {
    const payload = new FormData()

    payload.append('user_id', userId)

    Object.entries(form.value).forEach(([key, value]) => {
      if (key === 'photo') return
      if (value !== null && value !== '') {
        payload.append(key, value)
      }
    })

    if (photoFile.value) {
      payload.append('photo', photoFile.value)
    }

    const url = isEditMode.value
      ? `/employee/update-personal-info/${userId}`
      : `/employee/personal-information`

    await axios.post(url, payload, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })

    toast.success(
      isEditMode.value
        ? t('personalInfo.messages.updated')
        : t('personalInfo.messages.saved')
    )

    router.push({
      path: isEditMode.value
        ? '/employee/information/professional/edit'
        : '/employee/information/professional',
      query: { user_id: userId }
    })
  } catch (err) {
    if (err.response?.status === 422) {
      const errors = err.response.data.errors
      const messages = Object.values(errors).flat().join(', ')
      toast.error(messages)
    } else {
      toast.error('Failed to submit personal information.')
    }
  }
}

const handleBack = () => {
  router.push(isEditMode.value ? '/edit-employee' : '/add-employee')
}

const goNext = () => {
  router.push({
    path: '/employee/information/professional/edit',
    query: { user_id: userId }
  })
}

if (isEditMode.value) {
  onMounted(fetchData)
}
</script>

